import { useState, useEffect } from 'react';

/**
 * Hook to detect scroll position and show shadow on header
 * Uses requestAnimationFrame to prevent jank and excessive re-renders
 * 
 * @param offset - Scroll position in pixels to trigger shadow (default: 2)
 * @returns Object with hasShadow boolean
 * 
 * @example
 * const { hasShadow } = useScrollShadow(2);
 * <header className={hasShadow ? 'shadow-sm border-b' : ''}>
 */
export function useScrollShadow(offset = 2) {
  const [hasShadow, setHasShadow] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setHasShadow(window.scrollY > offset);
    };

    // Check initial scroll position
    onScroll();

    // Use RAF to throttle scroll events
    let ticking = false;
    const handler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handler, { passive: true });

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [offset]);

  return { hasShadow };
}
